import { Injectable } from '@angular/core';
import { BaseService } from './base';
import { HttpClient } from '@angular/common/http';
import { InventoryCatalog } from '../model/blueprint-list';
import { BasePaginator, Paginator } from '@vex/model/base/base-model';
import { environment } from 'environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { tap, finalize, share } from 'rxjs/operators';
@Injectable({
    providedIn: 'root'
})
export class InventoryService extends BaseService {
    readonly baseEndpoint = 'Inventory';
    private cache: any = <any>{};
    private cachedObservable: Observable<any> = <Observable<any>>{};

    constructor(private http: HttpClient) {
        super();
    }

    getAllVersionsByRecordGroupId(recordGroupId: string): Observable<any[]> {
        return this.http.get<any[]>(this.baseUrl + this.baseEndpoint + '/GetAllVersionsByRecordGroupId/' + recordGroupId);
    }

    getTerraformCodeById(id, status): Observable<any> {
        return this.http.get<any>(environment.WORKER_URL +'GenerateTerraformCodeByInventoryId/' + id + '/' + status);
    }

    getTerraformPakageById(id): Observable<any> {
        return this.http.get<any>(environment.WORKER_URL +'GenerateTerraformPackageByInventoryId/' + id);
    }

    getBriefById(id : string): Observable<any> {
        return this.http.get<InventoryCatalog>(
            this.baseUrl + this.baseEndpoint + '/GetBriefById/' + id
        );
    }

    /** Returns canonical device/inventory IDs for a possibly stale compliance assessment. */
    getComplianceNavigation(complianceHistoryId: string): Observable<any> {
        return this.http.get<any>(
            this.baseUrl + 'Device/GetComplianceNavigation/' + complianceHistoryId
        );
    }

    /** Returns canonical device/inventory IDs for a target in the normalized assessment store. */
    getComplianceTargetNavigation(runId: string, targetId: string): Observable<any> {
        return this.http.get<any>(
            this.baseUrl + 'ComplianceAssessments/' + encodeURIComponent(runId) +
            '/targets/' + encodeURIComponent(targetId) + '/navigation'
        );
    }

    getConfigurationResources(filter: any): Observable<BasePaginator<any>> {
        return this.http.post<BasePaginator<any>>(
            this.baseUrl + this.baseEndpoint + '/GetConfigurationResourcesPost',
            filter
        );
    }

    getConfigurationResourceById(inventoryId: string, resourceId: string): Observable<any> {
        return this.http.get<any>(
            this.baseUrl + this.baseEndpoint + '/GetConfigurationResourceById/' + inventoryId + '/' + resourceId
        );
    }

    getConfigurationTree(filter: any): Observable<any[]> {
        return this.http.post<any[]>(
            this.baseUrl + this.baseEndpoint + '/GetConfigurationTreePost',
            filter
        );
    }

    getConfigurationVisualization(inventoryId: string): Observable<any> {
        return this.http.get<any>(
            this.baseUrl + this.baseEndpoint + '/GetConfigurationVisualization/' + inventoryId
        );
    }

    generateAIDocument(inventoryId: string): Observable<any> {
        return this.http.get(this.baseUrl + this.baseEndpoint + '/GenerateAiDocument/' + inventoryId, { responseType: 'blob' });
    }

    GetTerraformStateFileByInventoryId(id: string): Observable<any> {
        return this.http.get<any>(environment.WORKER_URL +'GenerateTerraformStateByInventoryId/' + id);
    }

    GenerateTerraformCodeForDrift(id) {
        return this.http.get(environment.WORKER_URL + 'GenerateTerraformCodeForDrift/' + id);
    }

    postBluePrint(bluePrint: any[]) {
        return this.http.post(this.baseUrl + this.baseEndpoint + '', bluePrint);
    }

    postDistributedBluePrint(bluePrint: any[]) {
        return this.http.post(this.baseUrl + this.baseEndpoint + '/Distributed', bluePrint);
    }

    saveCodeFileInventory(bluePrint): Observable<any> {
        return this.http.post(this.baseUrl + this.baseEndpoint + '/SaveCodeFileInventory', bluePrint);
    }

    uploadCodeFile(formData: any) {
        return this.http.post(this.baseUrl + this.baseEndpoint + '/UploadCodeFile', formData, {
            reportProgress: true,
            observe: 'events'
        });
    }

    assignOwners(owners: any[], id) {
        return this.http.post(this.baseUrl + this.baseEndpoint + '/AssignOwners/'+id, owners);
    }

    getUserByIds(user: any): Observable<any> {
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/GetUserByIds', user);
    }

    getAvailableBlueprints<InventoryCatalog>(
        param: Paginator
    ): Observable<BasePaginator<InventoryCatalog>> {
        return this.http.post<BasePaginator<InventoryCatalog>>(
            this.baseUrl + this.baseEndpoint + '/GetPaginated',
            param
        );
    }

    getCatalogSummary(param: Paginator): Observable<any> {
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/GetCatalogSummary', param);
    }

    translateCatalogQuery(prompt: string, environmentType: number | null): Observable<{ SuggestedQuery: string }> {
        return this.http.post<{ SuggestedQuery: string }>(
            this.baseUrl + this.baseEndpoint + '/TranslateCatalogQuery',
            { Query: prompt, EnvironmentType: environmentType }
        );
    }

    getMatchingInventoryIds(param: Paginator): Observable<string[]> {
        return this.http.post<string[]>(this.baseUrl + this.baseEndpoint + '/GetCatalogIds', param);
    }

    pullConfigInventory(bpId: string): Observable<InventoryCatalog[]> {
        return this.http.get<InventoryCatalog[]>(
            this.baseUrl + this.baseEndpoint + '/PullConfigInventory/' + bpId
        );
    }

    GetInventoryForUpdate(bpId: string): Observable<InventoryCatalog[]> {
        return this.http.get<InventoryCatalog[]>(
            this.baseUrl + this.baseEndpoint + '/GetInventoryForUpdate/' + bpId
        );
    }

    editBluePrint(id, bluePrint: InventoryCatalog) {
        return this.http.put(this.baseUrl + this.baseEndpoint + '/' + id + '?id=' + id, bluePrint);
    }

    rollBackInventory(recordGroupId, version) {
        return this.http.get(
            this.baseUrl + this.baseEndpoint + '/Rollback/' + recordGroupId + '/' + version
        );
    }

    pullConfig(data) {
        return this.http.post(this.baseUrl + this.baseEndpoint + '/PullConfig', data);
    }

    syncInventory(id) {
        return this.http.get(this.baseUrl + this.baseEndpoint + '/Sync/' + id);
    }

    discoverNics(id) {
        return this.http.get(this.baseUrl + this.baseEndpoint + '/DiscoverNics/' + id);
    }

    pushConfig(id) {
        return this.http.get(this.baseUrl + this.baseEndpoint + '/PushConfig/' + id);
    }

    getMapView() {
        return this.http.get<any>(this.baseUrl + this.baseEndpoint + '/GetMapView');
    }

    getByVpcInfoId(vpcInfoId) {
        return this.http.get(
            this.baseUrl + this.baseEndpoint + '/GetByVpcInfoId?vpcInfoId=' + vpcInfoId
        );
    }

    getInventoryById(Id: string) {
        return this.http.get<InventoryCatalog>(this.baseUrl + this.baseEndpoint + '/GetById/' + Id);
    }

    getBlueprintCollectionsPaginatedPost(params): Observable<BasePaginator<any>> {
        return this.http.post<BasePaginator<any>>(
            this.baseUrl + this.baseEndpoint + '/GetBlueprintCollectionsPaginatedPost',
            params
        );
    }

    getBillingInfoByInventoryId(Id: string) {
        return this.http.get<InventoryCatalog>(this.baseUrl + this.baseEndpoint + '/GetBillingInfoByInventoryId/' + Id);
    }

    getInventoryForAccounts(region: any[]): Observable<any[]> {
        return this.http.post<any>(
            this.baseUrl + this.baseEndpoint + '/GetInventoriesForSelect',
            region
        );
    }

    updateWithIpPool(inventoryObj) {
        return this.http.post<any>(
            this.baseUrl + this.baseEndpoint + '/UpdateWithIpPool',
            inventoryObj
        );
    }

    getSubnetByInventoryId(Id: string) {
        return this.http.get<any[]>(
            this.baseUrl + this.baseEndpoint + '/GetSubnetsFromInventory/' + Id
        );
    }

    getVPCByRegion(Id: string) {
        return this.http.get<any[]>(
            this.baseUrl + this.baseEndpoint + '/GetVpcFromInventoryByRegion/' + Id
        );
    }

    getNICByInventoryId(Id: string) {
        return this.http.get<any[]>(
            this.baseUrl + this.baseEndpoint + '/GetNicsFromInventory/' + Id
        );
    }

    getHistory(recordGroupId: string): Observable<any[]> {
        return this.http.get<any[]>(this.baseUrl + this.baseEndpoint + '/GetAllVersions/' + recordGroupId);
    }

    getBackup(collectionEntryId: string): Observable<any[]> {
        return this.http.get<any[]>(
            this.baseUrl + this.baseEndpoint + '/GetAllBackups/' + collectionEntryId
        );
    }

    reviewInventory(review) {
        return this.http.post(this.baseUrl + this.baseEndpoint + '/ReviewInventory', review);
    }

    deleteInventory(data) {
        return this.http.post(this.baseUrl + this.baseEndpoint + '/DeleteInventory', data);
    }

    deleteInventories(data: any[]) {
        return this.http.post(this.baseUrl + this.baseEndpoint + '/DeleteInventories', data);
    }

    getInventoryByIds(ids) {
        return this.http.post<any[]>(this.baseUrl + this.baseEndpoint + '/GetByIds', ids);
    }

    getInventoryHistory(source: string, target: string) {
        if (!source) {
            return this.getInventoryHistoryBySource(target);
        }

        return this.http.get<any>(
            this.baseUrl + this.baseEndpoint + '/GetComparision/' + target + '/' + source
        );
    }

    parseTemplates(inventoryId: string) {
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/ParseTemplates/' + inventoryId, {});
    }

    getInventoryHistoryBySource(target: string) {
        return this.http.get<any>(this.baseUrl + this.baseEndpoint + '/GetComparision/' + target);
    }

    publishInventory(id) {
        if (!id) return throwError(new Error('An inventory ID is required to publish a deployment.'));
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/PublishInventory/' + encodeURIComponent(id), {});
    }

    publishDelete(id) {
        return this.http.get<any>(this.baseUrl + this.baseEndpoint + '/PublishDelete/' + id);
    }

    forceBackup(id) {
        return this.http.get<any>(this.baseUrl + this.baseEndpoint + '/ForceBackup/' + id);
    }

    checkNameAvailibility(name: string): Observable<string[]> {
        return this.http.get<string[]>(this.baseUrl + this.baseEndpoint + '/CheckName/' + encodeURIComponent(name));
    }

    getForSelectPaginated(params){
        let url = this.baseUrl + this.baseEndpoint + '/GetForSelectPaginated'
        return this.http.post<any>(url, params);
    }

    uploadInventoryImage(obj: any): Observable<any> {
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/UploadInventoryImage', obj);
    }

    getImage(path: string): Observable<any> {
        let url = this.baseUrl + this.baseEndpoint + '/DownloadImage';
        return this.http.post<any>(url, {Path: path});
    }

    getFileFromDocumentFolder(path: string): Observable<any> {
        return this.http.get(path, { responseType: 'blob' })
    }
    uploadConsoleRequestConfiguration(payload: any): Observable<any> {
        let url = this.baseUrl  + 'ConsoleRequest';
        return this.http.post<any>(url, payload);
    }

    getExecutionLogs(id): Observable<any> {
        return this.http.get<any>(this.baseUrl + this.baseEndpoint + '/GetExecutionLogs/' + id);
    }
      
    generateBicepByInventory(inventoryId): Observable<any> {
        return this.http.get<any>(this.baseUrl + this.baseEndpoint + '/GenerateBicepByInventory/' + inventoryId);
      }

    publishBicep(id:string, obj: any): Observable<any> {
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + `/PublishBicep/${id}`, obj);
    }  

    generateCliCode(id): Observable<any> {
        return this.http.get<any>(environment.WORKER_URL +'GenerateRawConfigByBlueprintId/' + id);
    }

    updateInventoryTags(payload: any): Observable<any> {
        let url = this.baseUrl + this.baseEndpoint + '/UpdateTags';
        return this.http.post<any>(url, payload);
    }

    getHubSpokeConnectionSummary(): Observable<any> {
        return this.http.get<any>(this.baseUrl + this.baseEndpoint +'/GetHubSpokeConnectionSummary');
    }
    
}
